import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { EVENT_TYPES, formatRange } from '../lib/format.js';

const EMPTY = {
  title: '',
  eventType: 'community_day',
  startsAt: '',
  endsAt: '',
  summary: '',
  description: '',
  bonusText: '',
  sourceName: '',
  sourceUrl: '',
  isPublished: false,
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api.admin.session()
      .then((d) => setAuthed(d.authenticated))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="skeleton" style={{ height: 200 }} />;
  if (!authed) return <LoginPanel onSuccess={() => setAuthed(true)} />;
  return <EventAdmin onLogout={() => setAuthed(false)} />;
}

function LoginPanel({ onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.admin.login(username, password);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ maxWidth: 400, margin: '48px auto' }}>
      <h2 style={{ marginBottom: 20 }}>Adminbereich</h2>

      <div className="field">
        <label className="field__label" htmlFor="u">Benutzername</label>
        <input id="u" className="input" value={username}
               onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="p">Passwort</label>
        <input id="p" className="input" type="password" value={password}
               onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
               onKeyDown={(e) => e.key === 'Enter' && submit()} />
      </div>

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

      <button className="btn btn--primary" onClick={submit} disabled={busy || !username || !password}>
        {busy ? 'Moment…' : 'Anmelden'}
      </button>
    </div>
  );
}

function EventAdmin({ onLogout }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () =>
    api.admin.listEvents().then((d) => setEvents(d.events)).catch((e) => setError(e.message));

  useEffect(() => { reload(); }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const reset = () => { setForm(EMPTY); setEditingId(null); setError(null); };

  const save = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      ...form,
      // Bonuszeilen als Liste — eine pro Zeile ist bei der Handpflege am schnellsten.
      bonuses: form.bonusText.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    delete payload.bonusText;

    try {
      if (editingId) await api.admin.updateEvent(editingId, payload);
      else await api.admin.createEvent(payload);
      reset();
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const edit = (event) => {
    setEditingId(event.id);
    setError(null);
    setForm({
      title: event.title,
      eventType: event.eventType,
      startsAt: toLocalInput(event.startsAt),
      endsAt: toLocalInput(event.endsAt),
      summary: event.summary ?? '',
      description: event.description ?? '',
      bonusText: (event.bonuses ?? []).join('\n'),
      sourceName: event.sourceName ?? '',
      sourceUrl: event.sourceUrl ?? '',
      isPublished: event.isPublished,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (event) => {
    if (!window.confirm(`"${event.title}" wirklich löschen?`)) return;
    await api.admin.deleteEvent(event.id);
    if (editingId === event.id) reset();
    await reload();
  };

  const logout = async () => { await api.admin.logout(); onLogout(); };

  return (
    <>
      <div className="spread" style={{ marginBottom: 24 }}>
        <h1>Events pflegen</h1>
        <button className="btn btn--ghost" onClick={logout}>Abmelden</button>
      </div>


      <div className="panel" style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 20 }}>{editingId ? 'Event bearbeiten' : 'Neues Event'}</h2>

        <div className="field">
          <label className="field__label">Titel</label>
          <input className="input" value={form.title} onChange={set('title')}
                 placeholder="z.B. Community Day: Karpador" />
        </div>

        <div className="row">
          <div className="field">
            <label className="field__label">Typ</label>
            <select className="select" value={form.eventType} onChange={set('eventType')}>
              {Object.entries(EVENT_TYPES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label">Kurzfassung</label>
            <input className="input" value={form.summary} onChange={set('summary')}
                   placeholder="Ein Satz für die Kartenansicht" />
          </div>
        </div>

        <div className="row">
          <div className="field">
            <label className="field__label">Start</label>
            <input className="input" type="datetime-local" value={form.startsAt} onChange={set('startsAt')} />
          </div>
          <div className="field">
            <label className="field__label">Ende</label>
            <input className="input" type="datetime-local" value={form.endsAt} onChange={set('endsAt')} />
          </div>
        </div>

        <div className="field">
          <label className="field__label">Beschreibung</label>
          <textarea className="textarea" value={form.description} onChange={set('description')} />
        </div>

        <div className="field">
          <label className="field__label">Boni — eine Zeile pro Bonus</label>
          <textarea className="textarea" value={form.bonusText} onChange={set('bonusText')}
                    placeholder={'3x Fang-Sternenstaub\n3 Stunden Rauchdauer'} />
        </div>

        <div className="row">
          <div className="field">
            <label className="field__label">Quelle</label>
            <input className="input" value={form.sourceName} onChange={set('sourceName')}
                   placeholder="z.B. Offizieller Blog" />
          </div>
          <div className="field">
            <label className="field__label">Quell-URL</label>
            <input className="input" type="url" value={form.sourceUrl} onChange={set('sourceUrl')} />
          </div>
        </div>

        <label className="inline" style={{ marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isPublished} onChange={set('isPublished')} />
          <span className="field__label" style={{ margin: 0 }}>Veröffentlicht</span>
        </label>

        {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="inline">
          <button className="btn btn--primary" onClick={save} disabled={busy || !form.title}>
            {busy ? 'Speichert…' : editingId ? 'Änderungen speichern' : 'Event anlegen'}
          </button>
          {editingId && <button className="btn btn--ghost" onClick={reset}>Abbrechen</button>}
        </div>
      </div>

      <h2 style={{ marginBottom: 16 }}>Angelegt ({events.length})</h2>

      {events.length === 0 ? (
        <div className="empty">Noch keine Events. Leg oben das erste an.</div>
      ) : (
        <div className="admin-list">
          {events.map((event) => (
            <div key={event.id} className={`admin-item${event.isPublished ? '' : ' admin-item--draft'}`}>
              <div>
                <div style={{ fontWeight: 700 }}>{event.title}</div>
                <div className="card__meta">
                  {EVENT_TYPES[event.eventType]} · {formatRange(event.startsAt, event.endsAt)}
                  {!event.isPublished && ' · Entwurf'}
                </div>
              </div>
              <div className="inline">
                <button className="btn btn--ghost" onClick={() => edit(event)}>Bearbeiten</button>
                <button className="btn btn--danger" onClick={() => remove(event)}>Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** 'YYYY-MM-DD HH:MM:SS' -> Wert für datetime-local. */
function toLocalInput(value) {
  return String(value).replace(' ', 'T').slice(0, 16);
}
